import { useState, useEffect } from 'react';
import { useSwrTick } from '@/lib/swrRefresh';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ChevronDown, ChevronUp, ExternalLink, AlertTriangle, ShieldAlert, TrendingDown, DollarSign, Repeat, Calendar, Droplets, Newspaper, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLeveragedCefs } from '@/hooks/useLeveragedCefs';
import DustLotsBanner from '@/components/DustLotsBanner';
import { AccountTypeBadge } from '@/components/AccountTypeModal';
import AccountTypeModal from '@/components/AccountTypeModal';
import { BrokerBadge } from '@/components/BrokerModal';
import BrokerModal from '@/components/BrokerModal';
import { useUserLimits } from '@/hooks/useUserLimits';
import { riskColorClasses, riskLabel, riskTooltip } from '@/lib/riskScore';
import { ccToFlag, ccName } from '@/lib/flagEmoji';
import DividendChangeBadge from '@/components/DividendChangeBadge';
import PreferredCallRiskChip from '@/components/PreferredCallRiskChip';
import QualityBadge from '@/components/QualityBadge';
import LeveragedCefIcon from '@/components/LeveragedCefIcon';
import { WarrantBadge } from '@/components/WarrantBadge';
import BadgeKey from '@/components/BadgeKey';
import TriangleScoreChip from '@/components/TriangleScoreChip';
import DripBuyToggleButton from '@/components/DripBuyToggleButton';
import QuickStockAddButton from '@/components/QuickStockAddModal';
import SingleMultiShareSell from '@/components/SingleMultiShareSell';
import {
  ProviderSentimentChip,
  NewsSentimentSummary,
  NewsSourceMix,
  MassiveInsightReasoning,
  KeywordChipRow,
  ActiveKeywordFilter,
} from '@/components/news/NewsBadges';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import { usePortfolioLoadStage } from '@/lib/portfolioLoadOrder';
import { useIsReadOnlyAllView } from '@/lib/useIsReadOnlyAllView';
import AllViewReadOnlyBanner from '@/components/AllViewReadOnlyBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round P108w (Feb 2026) — post-account-type-change: offer to re-tag
// historical dividend cash_transactions so the NIIT tile on Tax
// Audit / Year-End catches up. Silent no-op when nothing diverges.
async function _promptRetagPastDividends(retagTicker, prev, newType, activePortfolioId) {
  if (!newType || newType === prev) return;
  try {
    const params = { new_account_type: newType };
    if (activePortfolioId) params.portfolio_id = activePortfolioId;
    if (retagTicker) params.ticker = retagTicker;
    const { data } = await axios.get(`${API}/portfolio/dividends/retag-preview`, { params, withCredentials: true });
    const n = Number(data?.count) || 0;
    if (n <= 0) return;
    const scopeCopy = retagTicker
      ? `${n} past dividend${n === 1 ? '' : 's'} for ${retagTicker}`
      : `${n} past dividend${n === 1 ? '' : 's'} in this portfolio`;
    const newLabel = (newType || 'Untagged').replace(/_/g, ' ');
    toast(`${scopeCopy} still tagged with the old account type. Re-tag as ${newLabel}?`, {
      duration: 15000,
      action: {
        label: 'Re-tag',
        onClick: async () => {
          try {
            const body = { new_account_type: newType };
            if (activePortfolioId) body.portfolio_id = activePortfolioId;
            if (retagTicker) body.ticker = retagTicker;
            const { data: res } = await axios.post(`${API}/portfolio/dividends/retag`, body, { withCredentials: true });
            const modified = Number(res?.modified_count) || 0;
            toast.success(`Re-tagged ${modified} past dividend${modified === 1 ? '' : 's'} → ${newLabel}. NIIT figures on tax pages will refresh.`);
            window.dispatchEvent(new Event('portfolio-changed'));
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not re-tag past dividends');
          }
        },
      },
    });
  } catch {
    // silent — the primary save succeeded; this nudge is best-effort
  }
}

// Calculate days between today and a date string like "May 06, 2026".
// Returns null if the date can't be parsed or is in the past.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  return diff;
}

// "Updated Xs ago" label removed (May 2026) when Massive WS went live —
// the helper and 30s re-render tick were only used to animate that label.

// Compute the next ex-div date from a past ex-div date + frequency.
// Used as a fallback when the backend (e.g. Massive) didn't supply next_ex_date.
function computeNextExDate(exStr, frequency) {
  if (!exStr) return null;
  const base = new Date(exStr);
  if (isNaN(base.getTime())) return null;
  // P158·8 (Feb 2026) — Handle Daily payers (SATA, MSTY-style
  // 252-business-day funds). Without this, Daily fell through to the
  // default 3-month bump so a daily payer whose last ex-date was 2
  // business days ago got labelled "Ex-div 89d". The Daily branch
  // walks one calendar day at a time and the trailing weekend-skip
  // block below lands the pill on the next weekday (Mon after Sat/Sun).
  const isDaily = frequency === 'Daily';
  const months = frequency === 'Monthly' ? 1
    : frequency === 'Weekly' ? 0
    : frequency === 'Semi-Annually' ? 6
    : frequency === 'Annually' ? 12
    : isDaily ? 0
    : 3;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const next = new Date(base);
  let safety = 0;
  const maxIter = isDaily ? 400 : 120;
  while (next <= now && safety < maxIter) {
    if (isDaily) next.setDate(next.getDate() + 1);
    else if (months === 0) next.setDate(next.getDate() + 7);
    else next.setMonth(next.getMonth() + months);
    safety++;
  }
  // Skip weekends
  while (next.getDay() === 0 || next.getDay() === 6) {
    next.setDate(next.getDate() + 1);
  }
  return next.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

function ExDivPill({ stockData }) {
  // Aug 2026 (IGD bug) — Prefer backend `next_ex_date`, but if it's
  // already past (backend cron hasn't rolled it forward yet), fall
  // through to computing the next occurrence from the LAST paid
  // ex-date + frequency. IGD's Aug 3 ex-date sat there stale for
  // 12+ days after payment while the est-next-date job lagged
  // behind Massive events, hiding the pill entirely for a monthly
  // payer that clearly HAS a next date.
  let nextDateStr = stockData?.next_ex_date || null;
  let days = daysUntil(nextDateStr);
  if (nextDateStr === null || days === null || days < 0) {
    const computed = computeNextExDate(stockData?.ex_dividend_date, stockData?.frequency);
    const cDays = daysUntil(computed);
    if (computed && cDays !== null && cDays >= 0) {
      nextDateStr = computed;
      days = cDays;
    }
  }
  if (days === null || days < 0) return null;

  let dotColor = 'bg-emerald-500';
  if (days <= 2) dotColor = 'bg-red-500';
  else if (days <= 7) dotColor = 'bg-amber-500';

  const label = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `${days}d`;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-muted/60 border border-border/50"
      title={`Next ex-div: ${nextDateStr}`}
      data-testid="ex-div-pill"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      Ex-div {label}
    </span>
  );
}

function PositionRow({ position, onRemove, onSell, onMarkedDrip, onAccountTypeChanged, onBrokerChanged, ticker, stockPrice, isAllReadOnly = false, isNonDiv = false }) {
  const [selling, setSelling] = useState(false);
  const [sShares, setSShares] = useState('');
  const [sPrice, setSPrice] = useState('');
  const [sDate, setSDate] = useState('');
  const [sErr, setSErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [acctType, setAcctType] = useState(position.account_type || 'taxable');
  const [brokerOpen, setBrokerOpen] = useState(false);
  const [broker, setBroker] = useState(position.broker || null);
  // Round P108v (Feb 2026) — scope apply-all + apply_all=true PATCHes
  // to the active portfolio so a "change all AAPL lots" click never
  // stomps AAPL lots in a sibling portfolio.
  const _scopeParams = usePortfolioScopeParams();
  const _activePortfolioId = _scopeParams.portfolio_id || null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const startSell = () => {
    setSShares(String(position.shares));
    setSPrice(stockPrice ? String(stockPrice.toFixed(2)) : '');
    setSDate(new Date().toISOString().split('T')[0]);
    setSErr('');
    setSelling(true);
  };
  const cancelSell = () => {
    setSelling(false);
    setSErr('');
  };

  const confirmSell = async () => {
    // Round 71m (Feb 2026): clamp to 4 decimals so a 6-decimal value
    // pre-filled from `position.shares` (legacy lots could have 6dp
    // before the 2026-02 normalize migration ran) doesn't bypass the
    // "stored to 4 decimal places" promise on the way back to the
    // backend.
    const n = Math.round(Number(sShares) * 1e4) / 1e4;
    const p = Number(sPrice);
    if (!n || n <= 0) return setSErr('Enter shares to sell');
    if (n > position.shares + 1e-9) return setSErr(`Only ${position.shares} shares available`);
    if (!p || p <= 0) return setSErr('Enter a sale price');
    setBusy(true);
    try {
      await onSell?.(ticker, position.position_id, { shares: n, sale_price: p, sale_date: sDate || new Date().toISOString().split('T')[0] });
      setSelling(false);
    } catch (e) {
      setSErr(e?.response?.data?.detail || 'Failed to sell');
    }
    setBusy(false);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-border/50 flex-wrap" data-testid="portfolio-position-row">
        <div className="text-sm flex items-center gap-x-2 gap-y-0.5 flex-wrap">
          {/* Round 97a (Feb 2026) — DRIP toggle is dividend-only. For
              non-dividend positions there's no cash payout to reinvest,
              so the hollow water-drop button (and the DRIP pill once
              marked) are suppressed entirely. */}
          {!isNonDiv && (isAllReadOnly ? (
            <Droplets className={`w-4 h-4 ${position.is_drip ? 'text-sky-500' : 'text-muted-foreground/40'}`} />
          ) : (
            <DripBuyToggleButton
              ticker={ticker}
              positionId={position.position_id}
              isDrip={!!position.is_drip}
              size="w-4 h-4"
              testIdSuffix={`portfolio-${position.position_id}`}
              onMarked={(updated) => onMarkedDrip?.(ticker, position.position_id, updated)}
            />
          ))}
          {!isNonDiv && position.is_drip && (
            <span
              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-400"
              title="DRIP buy — paid for with dividend cash from this or another holding."
              data-testid="portfolio-drip-badge"
            >
              <Droplets className="w-3 h-3" />
              <span className="text-[9px] font-bold uppercase tracking-wider">DRIP</span>
            </span>
          )}
          <span className="font-medium whitespace-nowrap">{position.shares} shares</span>
          <span className="text-muted-foreground whitespace-nowrap">@ ${position.cost_per_share?.toFixed(2)}</span>
          <span className="text-muted-foreground/60 text-xs whitespace-nowrap">Purchased: {formatDate(position.date_purchased)}</span>
          <AccountTypeBadge
            value={acctType}
            onClick={() => setAcctOpen(true)}
            testId={`portfolio-account-type-${position.position_id}`}
          />
          <BrokerBadge
            value={broker}
            onClick={() => setBrokerOpen(true)}
            testId={`portfolio-broker-${position.position_id}`}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isAllReadOnly && (
            <>
              <button
                onClick={selling ? cancelSell : startSell}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 transition-colors"
                data-testid="portfolio-sell-position"
              >
                {selling ? 'Cancel' : 'Sell Shares'}
              </button>
              <button
                onClick={() => onRemove(position.position_id)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/60 transition-colors"
                data-testid="portfolio-remove-position"
                title="Remove from portfolio (no cash recorded)"
              >
                Remove from Portfolio
              </button>
            </>
          )}
        </div>
      </div>
      {selling && (
        <div className="px-4 py-2 bg-muted/30 border-t border-border/40" data-testid="portfolio-sell-form">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Shares</label>
              <input
                type="number" min="0" max={position.shares} step="0.0001"
                value={sShares}
                onChange={(e) => setSShares(e.target.value)}
                className="w-20 px-2 py-1 text-xs rounded border bg-background"
                data-testid="portfolio-sell-shares"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">$/share</label>
              <input
                type="number" min="0" step="any"
                value={sPrice}
                onChange={(e) => setSPrice(e.target.value)}
                className="w-24 px-2 py-1 text-xs rounded border bg-background"
                data-testid="portfolio-sell-price"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Date</label>
              <input
                type="date"
                value={sDate}
                onChange={(e) => setSDate(e.target.value)}
                className="px-2 py-1 text-xs rounded border bg-background"
                data-testid="portfolio-sell-date"
              />
            </div>
            <div className="text-[10px] text-muted-foreground">
              Proceeds: <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                ${((Number(sShares) || 0) * (Number(sPrice) || 0)).toFixed(2)}
              </span>
            </div>
            <button
              onClick={confirmSell}
              disabled={busy}
              className="ml-auto px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60"
              data-testid="portfolio-sell-confirm"
            >
              {busy ? 'Selling...' : 'Confirm Sell'}
            </button>
          </div>
          {sErr && <p className="mt-1 text-[10px] text-red-500">{sErr}</p>}
        </div>
      )}
      <AccountTypeModal
        open={acctOpen}
        currentValue={acctType}
        ticker={ticker}
        onSave={async (newType, opts = {}) => {
          const prev = acctType;
          const scope = opts.scope || 'lot';
          setAcctType(newType);
          try {
            if (scope === 'portfolio') {
              // Whole-portfolio bulk: dedicated endpoint
              const { data } = await axios.post(
                `${API}/portfolio/account-type/apply-all`,
                { account_type: newType, portfolio_id: _activePortfolioId },
                { withCredentials: true }
              );
              const n = data?.modified_count ?? data?.matched_count ?? 0;
              toast.success(`Updated ${n} lot${n === 1 ? '' : 's'} across your entire portfolio → ${newType.replace('_', ' ')}`);
              // Force a portfolio-level refresh so every sibling
              // PositionRow + the year-end summary refetch.
              onAccountTypeChanged?.(ticker, position.position_id, newType, { applyAll: true, applyPortfolio: true });
              window.dispatchEvent(new Event('portfolio-changed'));
              _promptRetagPastDividends(null, prev, newType, _activePortfolioId);
              return;
            }
            const applyAll = scope === 'all';
            const { data } = await axios.patch(
              `${API}/portfolio/${ticker}/positions/${position.position_id}/account-type`,
              { account_type: newType, apply_all: applyAll, portfolio_id: _activePortfolioId },
              { withCredentials: true }
            );
            if (applyAll) {
              const n = data?.modified_count ?? data?.matched_count ?? 0;
              toast.success(`Updated ${n} ${ticker} lot${n === 1 ? '' : 's'} → ${newType.replace('_', ' ')}`);
            } else {
              toast.success(`Account set to ${newType.replace('_', ' ')}`);
            }
            onAccountTypeChanged?.(ticker, position.position_id, newType, { applyAll });
            _promptRetagPastDividends(ticker, prev, newType, _activePortfolioId);
          } catch (e) {
            setAcctType(prev);
            toast.error(e?.response?.data?.detail || 'Could not save account type');
          }
        }}
        onClose={() => setAcctOpen(false)}
        title={`Account for ${position.shares} sh of ${ticker}`}
      />
      <BrokerModal
        open={brokerOpen}
        currentValue={broker}
        ticker={ticker}
        onSave={async (newBroker, opts = {}) => {
          const prev = broker;
          const scope = opts.scope || 'lot';
          setBroker(newBroker);
          try {
            if (scope === 'portfolio') {
              const { data } = await axios.post(
                `${API}/portfolio/broker/apply-all`,
                { broker: newBroker, portfolio_id: _activePortfolioId },
                { withCredentials: true }
              );
              const n = data?.modified_count ?? data?.matched_count ?? 0;
              const label = newBroker || 'Untagged';
              toast.success(`Updated ${n} lot${n === 1 ? '' : 's'} across your entire portfolio → ${label}`);
              onBrokerChanged?.(ticker, position.position_id, newBroker, { applyAll: true, applyPortfolio: true });
              window.dispatchEvent(new Event('portfolio-changed'));
              return;
            }
            const applyAll = scope === 'all';
            const { data } = await axios.patch(
              `${API}/portfolio/${ticker}/positions/${position.position_id}/broker`,
              { broker: newBroker, apply_all: applyAll, portfolio_id: _activePortfolioId },
              { withCredentials: true }
            );
            const label = newBroker || 'Untagged';
            if (applyAll) {
              const n = data?.modified_count ?? data?.matched_count ?? 0;
              toast.success(`Updated ${n} ${ticker} lot${n === 1 ? '' : 's'} → ${label}`);
            } else {
              toast.success(`Broker set to ${label}`);
            }
            onBrokerChanged?.(ticker, position.position_id, newBroker, { applyAll });
          } catch (e) {
            setBroker(prev);
            toast.error(e?.response?.data?.detail || 'Could not save broker');
          }
        }}
        onClose={() => setBrokerOpen(false)}
        title={`Broker for ${position.shares} sh of ${ticker}`}
      />
    </>
  );
}

function NewsInline({ ticker }) {
  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-row keyword filter — clicking a Massive `#keyword` chip on
  // any article narrows the strip for THIS ticker only. Filter
  // auto-clears whenever the ticker changes (different stock row
  // expanded into the inline news view).
  const [selectedKeyword, setSelectedKeyword] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedKeyword(null);
    // Pull 5 articles (was 3). With the backend's Massive→Yahoo
    // interleave, top-5 = exactly 3 Massive + 2 Yahoo. Finnhub fills
    // when one provider thins out (backend threshold: combined < 6).
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setArticles(res.data?.articles?.slice(0, 5) || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [ticker]);

  const filtered = selectedKeyword
    ? (articles || []).filter((a) => {
        const kws = Array.isArray(a.keywords) ? a.keywords : [];
        return kws.some((k) => (k || '').toLowerCase() === selectedKeyword.toLowerCase());
      })
    : (articles || []);

  if (loading) {
    return (
      <div className="px-4 py-3 border-t border-border/30">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <div className="px-4 py-3 border-t border-border/30 text-xs text-muted-foreground text-center">
        No news available for {ticker}
      </div>
    );
  }

  return (
    <div className="border-t border-border/30 divide-y divide-border/20" data-testid={`news-inline-${ticker}`}>
      {/* Compact "vibe" pill at the top of the inline news strip when we
          have at least 2 sentiment-classified articles. Same component
          used at the top of the StockModal NewsTab so the user gets a
          consistent quick-read across all surfaces. */}
      {articles.length >= 2 && (
        <div className="px-4 py-1.5 flex items-center justify-end" data-testid={`news-inline-vibe-${ticker}`}>
          <NewsSentimentSummary articles={articles} />
        </div>
      )}
      {selectedKeyword && (
        <div className="px-4 py-1.5">
          <ActiveKeywordFilter
            keyword={selectedKeyword}
            matchCount={filtered.length}
            totalCount={articles.length}
            onClear={() => setSelectedKeyword(null)}
          />
        </div>
      )}
      {filtered.length === 0 ? (
        <div className="px-4 py-3 text-xs text-muted-foreground text-center">
          No articles match #{selectedKeyword} for {ticker}.
        </div>
      ) : filtered.map((article, i) => (
        <div
          key={i}
          className="px-4 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors group"
          onClick={() => {
            const url = article.url || `https://www.google.com/search?q=${encodeURIComponent(article.title)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          data-testid={`news-inline-article-${ticker}-${i}`}
        >
          <div className="flex items-start gap-2">
            {/* Yahoo News thumbnail (when present) — sized to match
                the title + summary block height so it doesn't add
                extra vertical space. onError hides broken / blocked
                images so the row falls back to the original layout
                without leaving a gray placeholder. Sep 26 2026 —
                object-contain so publisher wordmark thumbs render
                whole instead of cropped-center. */}
            {article.thumbnail && (
              <img
                src={article.thumbnail}
                alt=""
                loading="lazy"
                className="w-10 h-10 rounded object-contain shrink-0 bg-muted p-0.5"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid={`news-inline-thumb-${ticker}-${i}`}
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2">{article.title}</p>
              <KeywordChipRow article={article} selected={selectedKeyword} onToggle={setSelectedKeyword} />
              {article.summary && (
                <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{article.summary}</p>
              )}
              <MassiveInsightReasoning article={article} compact />
              <div className="flex items-center gap-2 mt-1 text-[9px] text-muted-foreground flex-wrap">
                <span>{article.source}</span>
                <ProviderSentimentChip
                  provider={article.provider}
                  sentiment={article.sentiment}
                  compact
                />
                <span>{article.age}</span>
              </div>
            </div>
            <ExternalLink className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-0.5 transition-colors" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Compute the estimated $ received from a dividend payout for a given
// position. Round 95j (Jun 2026) — for variable-distribution funds
// (YieldMax weekly ETFs like TSMY, semi-annual ADRs, foreign-currency
// REITs) `dividend_rate / per-year` annualizes the forward rate, which
// can be far off the actual last per-share cash. The user-reported
// repro: TSMY paid $0.1488/share last week but `dividend_rate / 52`
// projected $0.1766/share → 16-share row showed ~$2.83 vs the real
// $2.38. Prefer `last_dividend_value × totalShares` when present (this
// is the exchange-sourced per-share for the most recent ex-div the
// banner is announcing) and only fall back to the rough annualized
// estimator when no last-per-share is available.
function estimatePayoutAmount(totalShares, stockData) {
  const lastPerShare = Number(stockData?.last_dividend_value || 0);
  if (lastPerShare > 0 && totalShares > 0) {
    return Math.max(0, totalShares * lastPerShare);
  }
  const divRate = stockData?.dividend_rate || 0;
  const freq = stockData?.frequency || 'Quarterly';
  const perYear = freq === 'Monthly' ? 12
    : freq === 'Weekly' ? 52
    : freq === 'Semi-Annually' ? 2
    : freq === 'Annually' ? 1
    : 4; // Quarterly default
  const perPayout = divRate / perYear;
  return Math.max(0, totalShares * perPayout);
}

// Return true if `dateStr` (e.g. "Apr 29, 2026" or ISO) is in the past
// but within `maxDays`. This is the window where we show the
// "You received a dividend payout!" reminder.
//
// Sep 2026 — `maxDays` is now caller-supplied. 14 days is right for a
// quarterly or monthly payer, but a DAILY payer (SATA) generates a
// payout every business day, and a user who doesn't open the app for a
// month would silently lose ~20 of them. Daily/weekly holdings get the
// full 90-day window the value-history fetch already covers.
function isRecentPayoutWindow(dateStr, maxDays = 14) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const daysAgo = Math.round((now - d) / (1000 * 60 * 60 * 24));
  return daysAgo >= 0 && daysAgo <= maxDays;
}

// Sep 2026 — How far apart two payouts of this ticker can be before
// the dismissal matcher is allowed to treat them as the same event.
// The ±3 day tolerance below exists to absorb pay-vs-ex format drift,
// which is safe at 7+ day spacing — but on a DAILY payer it meant
// actioning Aug 31 also silenced Sep 1, 2 and 3. That is exactly why
// the user saw the prompt once and never again on a daily dividend.
function payoutMatchTolerance(frequency) {
  const f = String(frequency || '').toLowerCase();
  if (f === 'daily') return 0;
  if (f === 'weekly') return 2;
  return 3;
}

// Payout window in days for a given cadence (see isRecentPayoutWindow).
function payoutWindowDays(frequency) {
  const f = String(frequency || '').toLowerCase();
  return (f === 'daily' || f === 'weekly') ? 90 : 14;
}

// Round 95j (Jun 2026) — Normalize any payout-date string ("May 28,
// 2026", "2026-05-28", "5/28/2026") to YYYY-MM-DD using LOCAL date
// components, not UTC ISO. Previously `d.toISOString().slice(0,10)`
// shifted "Jun 05, 2026" → "2026-06-04" for users east of UTC because
// `new Date("Jun 05, 2026")` parses to local midnight, then ISO casts
// back to UTC. That broke the DividendPayoutRow lookup against the
// dividend-calendar (which is keyed by the exchange's pay_date ISO),
// causing the banner to fall back to the rough estimator and show the
// wrong $ for TSMY-style variable weekly ETFs. Reading the parts via
// `getFullYear/getMonth/getDate` yields the date the user typed,
// regardless of their TZ, which matches what the backend stores.
function _toIsoDate(s) {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  } catch {}
  return null;
}

// Returns true if the payout for `payDate` has already been actioned
// or dismissed. `tolDays` guards against format drift between ex-date
// and pay-date; it must be 0 on a daily payer (see
// payoutMatchTolerance) or actioning one day silences the next three.
function isPayoutDismissed(dismissedSet, payDate, tolDays = 3) {
  if (!dismissedSet || dismissedSet.size === 0) return false;
  const targetIso = _toIsoDate(payDate);
  if (!targetIso) return false;
  if (dismissedSet.has(targetIso)) return true;
  if (tolDays <= 0) return false;
  const target = new Date(targetIso).getTime();
  for (const iso of dismissedSet) {
    const t = new Date(iso).getTime();
    if (!isNaN(t)) {
      const diffDays = Math.abs((target - t) / (1000 * 60 * 60 * 24));
      if (diffDays <= tolDays) return true;
    }
  }
  return false;
}

// DividendPayoutRow — renders a rose-tinted banner between the stock
// header and the positions list whenever the ticker has paid out
// within the last 14 days AND the user hasn't already actioned or
// dismissed that specific payout. Gives three actions:
//   • Add DRIP Buy — opens a compact form (shares / price / date) and
//     records a new cost-basis lot via POST /dividend-payout/drip-buy
//   • Add to Cash — opens a 1-field amount form (pre-filled with the
//     estimate) and credits cash via POST /dividend-payout/add-to-cash
//   • Dismiss (X) — hides the row permanently via
//     POST /dividend-payout/dismiss
function DividendPayoutRow({ ticker, stockData, totalShares, payoutDate, onActioned, isAllReadOnly = false, actualPayoutsByDate = null, compact = false }) {
  const [mode, setMode] = useState(null); // null | 'drip' | 'cash'
  const [submitting, setSubmitting] = useState(false);
  // Sep 2026 — hide the row the instant an action succeeds instead of
  // waiting for the dismissals refetch (which can be slow or fail
  // silently). Otherwise the still-live form let the same payout be
  // added to cash / DRIP-bought twice (user's SATA 09-10 double buy).
  const [done, setDone] = useState(false);
  // Prefer the calendar-derived actual paid amount (scoped by portfolio_id
  // and computed off shares-at-ex-div × per-share) for the EXACT pay date
  // this banner is announcing. `actualPayoutsByDate` is `{ ISOdate: amount }`
  // for the ticker. Falls back to the rough estimator only when no
  // matching calendar event exists (brand-new payouts pending confirm).
  const fallbackEstimated = estimatePayoutAmount(totalShares, stockData);
  const estimated = (() => {
    if (!actualPayoutsByDate) return fallbackEstimated;
    // Round 95j (Jun 2026) — use the same TZ-safe local-date parser so
    // the calendar lookup works in every timezone (otherwise "Jun 05,
    // 2026" was being ISO'd to Jun 04 UTC for east-of-UTC users and the
    // banner fell back to the rough estimator — the TSMY $2.83 bug).
    const payIso = _toIsoDate(payoutDate) || '';
    if (payIso && Number(actualPayoutsByDate[payIso] || 0) > 0) {
      return Number(actualPayoutsByDate[payIso]);
    }
    return fallbackEstimated;
  })();
  const today = new Date().toISOString().slice(0, 10);
  // Sep 2026 — default the buy date to the PAYOUT's pay date, not
  // today. On a backlogged daily-payer queue the rows can be weeks
  // old, and dating every reinvested lot "today" mis-states the cost
  // basis and the holding period.
  const dripDefaultDate = _toIsoDate(payoutDate) || today;
  // Form state — DRIP has shares/price/date; Cash has amount.
  // Prefill shares = payout ÷ price so a $0.05 payout at $99.99 reads
  // 0.0005, not a hand-typed 0.0053; still editable.
  const [dripShares, setDripShares] = useState(() => {
    const px = Number(stockData?.price) || 0;
    return estimated > 0 && px > 0 ? (estimated / px).toFixed(4) : '';
  });
  const [dripPrice, setDripPrice] = useState(stockData?.price ? Number(stockData.price).toFixed(2) : '');
  const [dripDate, setDripDate] = useState(dripDefaultDate);
  const [cashAmount, setCashAmount] = useState(estimated > 0 ? estimated.toFixed(2) : '');

  const handleDismiss = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/portfolio/dividend-payout/dismiss`, {
        ticker, payout_date: payoutDate, amount: estimated,
      }, { withCredentials: true });
      toast.success('Reminder dismissed');
      setDone(true);
      onActioned?.();
    } catch (e) {
      toast.error('Could not dismiss', { description: e?.response?.data?.detail || e?.message });
    } finally { setSubmitting(false); }
  };

  const handleCashSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(cashAmount);
    if (!(amt > 0)) { toast.error('Enter an amount > 0'); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API}/portfolio/dividend-payout/add-to-cash`, {
        ticker, payout_date: payoutDate, amount: amt,
      }, { withCredentials: true });
      toast.success(`+$${amt.toFixed(2)} added to cash balance`, {
        description: 'Capital Tracker → Cash Balance updated.',
      });
      window.dispatchEvent(new Event('cash-changed'));
      setDone(true);
      onActioned?.();
    } catch (err) {
      toast.error('Could not add to cash', { description: err?.response?.data?.detail || err?.message });
    } finally { setSubmitting(false); }
  };

  const handleDripSubmit = async (e) => {
    e.preventDefault();
    const shares = parseFloat(dripShares);
    const price = parseFloat(dripPrice);
    if (!(shares > 0) || !(price > 0)) { toast.error('Shares and price must be > 0'); return; }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/portfolio/dividend-payout/drip-buy`, {
        ticker, payout_date: payoutDate, shares, price, date_purchased: dripDate,
      }, { withCredentials: true });
      // Surface whichever accounting branch the backend picked:
      //   cash_mode='inside'  → dividend cash used as expected (happy path)
      //   cash_mode='drip'    → cash was short, logged informationally
      if (data?.cash_warning) {
        toast.warning(`DRIP buy saved: +${shares} ${ticker} @ $${price.toFixed(2)}`, {
          description: data.cash_warning,
          duration: 8000,
        });
      } else {
        toast.success(`DRIP buy recorded: +${shares} ${ticker} @ $${price.toFixed(2)}`, {
          description: `$${(shares * price).toFixed(2)} dividend reinvested — new lot added under ${ticker}. No cash deducted.`,
        });
      }
      // Let other cards (Cash Balance, Capital Tracker) refresh.
      window.dispatchEvent(new Event('cash-changed'));
      setDone(true);
      onActioned?.();
    } catch (err) {
      toast.error('Could not record DRIP buy', { description: err?.response?.data?.detail || err?.message });
    } finally { setSubmitting(false); }
  };

  if (done) return null;
  return (
    <div
      className={compact
        ? 'relative px-4 py-2.5'
        : 'relative bg-emerald-50/70 dark:bg-emerald-950/30 border-t border-b border-emerald-300/50 dark:border-emerald-800/60 px-4 py-3'}
      data-testid={`portfolio-payout-row-${ticker}`}
      data-payout-date={_toIsoDate(payoutDate) || payoutDate}
    >
      <button
        type="button"
        onClick={handleDismiss}
        disabled={submitting}
        className="absolute top-1.5 right-1.5 p-1 rounded text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200/60 dark:hover:bg-emerald-900/60 disabled:opacity-50"
        title="Dismiss reminder"
        data-testid={`portfolio-payout-dismiss-${ticker}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-center gap-2 flex-wrap">
        <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
        <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200">
          {compact ? `Paid ${payoutDate}` : 'You received a dividend payout!'}
        </span>
        {estimated > 0 && (
          <span className="text-[11px] font-mono font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded">
            ~${estimated.toFixed(2)}
          </span>
        )}
        <span className="text-[10px] text-emerald-700/80 dark:text-emerald-300/80">
          {compact ? `${totalShares > 0 ? `${totalShares} sh` : ''}` : `paid ${payoutDate}`}
        </span>
      </div>

      {!mode && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {isAllReadOnly ? (
            <AllViewReadOnlyBanner verb="reinvest this dividend or add it to cash" compact />
          ) : (
            <>
              <button
                type="button"
                onClick={() => setMode('drip')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-violet-600 hover:bg-violet-700 text-white"
                data-testid={`portfolio-payout-drip-open-${ticker}`}
              >
                <Repeat className="w-3 h-3" /> Add DRIP Buy
              </button>
              <button
                type="button"
                onClick={() => setMode('cash')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`portfolio-payout-cash-open-${ticker}`}
              >
                <DollarSign className="w-3 h-3" /> Add to Cash
              </button>
            </>
          )}
        </div>
      )}

      {mode === 'cash' && (
        <form
          onSubmit={handleCashSubmit}
          className="mt-2 flex items-center gap-2 flex-wrap"
          data-testid={`portfolio-payout-cash-form-${ticker}`}
        >
          <label className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-200">Amount $</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={cashAmount}
            onChange={(e) => setCashAmount(e.target.value)}
            className="w-24 px-2 py-1 text-sm rounded border border-emerald-400/40 bg-background"
            data-testid={`portfolio-payout-cash-amount-${ticker}`}
            autoFocus
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid={`portfolio-payout-cash-add-${ticker}`}
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setMode(null)}
            className="px-2 py-1 rounded-md text-[11px] text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200/60 dark:hover:bg-emerald-900/40"
          >
            Cancel
          </button>
        </form>
      )}

      {mode === 'drip' && (
        <form
          onSubmit={handleDripSubmit}
          className="mt-2 flex items-center gap-2 flex-wrap"
          data-testid={`portfolio-payout-drip-form-${ticker}`}
        >
          <label className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-200">Shares</label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={dripShares}
            onChange={(e) => setDripShares(e.target.value)}
            className="w-20 px-2 py-1 text-sm rounded border border-emerald-400/40 bg-background"
            data-testid={`portfolio-payout-drip-shares-${ticker}`}
            autoFocus
            required
          />
          <label className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-200">Price $</label>
          <input
            type="number"
            step="any"
            min="0"
            value={dripPrice}
            onChange={(e) => setDripPrice(e.target.value)}
            className="w-24 px-2 py-1 text-sm rounded border border-emerald-400/40 bg-background"
            data-testid={`portfolio-payout-drip-price-${ticker}`}
            required
          />
          <label className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-200">Date</label>
          <input
            type="date"
            value={dripDate}
            onChange={(e) => setDripDate(e.target.value)}
            className="px-2 py-1 text-sm rounded border border-emerald-400/40 bg-background"
            data-testid={`portfolio-payout-drip-date-${ticker}`}
            required
          />
          <button
            type="button"
            onClick={() => setDripDate(today)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-emerald-200/60 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-300/60"
            data-testid={`portfolio-payout-drip-today-${ticker}`}
          >
            <Calendar className="w-3 h-3" /> Today
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
            data-testid={`portfolio-payout-drip-add-${ticker}`}
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setMode(null)}
            className="px-2 py-1 rounded-md text-[11px] text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200/60 dark:hover:bg-emerald-900/40"
          >
            Cancel
          </button>
          {(() => {
            const cost = (parseFloat(dripShares) || 0) * (parseFloat(dripPrice) || 0);
            if (!(cost > 0)) return null;
            const over = estimated > 0 && cost > estimated * 1.1;
            return (
              <span className={`text-[10px] font-mono ${over ? 'text-rose-600 dark:text-rose-400 font-bold' : 'text-emerald-700/80 dark:text-emerald-300/80'}`} data-testid={`portfolio-payout-drip-cost-${ticker}`}>
                ≈ ${cost.toFixed(2)}{estimated > 0 ? ` of ~$${estimated.toFixed(2)} payout` : ''}{over ? ' — more than the dividend paid, check the shares' : ''}
              </span>
            );
          })()}
        </form>
      )}
    </div>
  );
}

// Sep 2026 — DividendPayoutQueue. A daily payer (SATA) drops a payout
// every business day, so a single banner showing only the latest event
// was structurally wrong: the user marked Aug 31 as a DRIP buy and
// Sep 1 never appeared. This renders EVERY un-actioned payout in the
// window — one banner when there is one, a collapsible queue when
// there are several — so nothing has to be caught the day it lands.
function DividendPayoutQueue({ ticker, stockData, events, onActioned, isAllReadOnly, actualPayoutsByDate }) {
  const [open, setOpen] = useState(false);
  if (!events || events.length === 0) return null;
  if (events.length === 1) {
    const ev = events[0];
    return (
      <DividendPayoutRow
        ticker={ticker}
        stockData={stockData}
        totalShares={ev.shares}
        payoutDate={ev.payDate}
        onActioned={onActioned}
        isAllReadOnly={isAllReadOnly}
        actualPayoutsByDate={actualPayoutsByDate}
      />
    );
  }
  const total = events.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  return (
    <div
      className="border-t border-b border-emerald-300/50 dark:border-emerald-800/60 bg-emerald-50/70 dark:bg-emerald-950/30"
      data-testid={`portfolio-payout-queue-${ticker}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 flex-wrap px-4 py-2.5 text-left hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30 transition-colors"
        data-testid={`portfolio-payout-queue-toggle-${ticker}`}
      >
        <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
        <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200">
          {events.length} dividend payouts waiting
        </span>
        {total > 0 && (
          <span className="text-[11px] font-mono font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded">
            ~${total.toFixed(2)} total
          </span>
        )}
        <span className="text-[10px] text-emerald-700/80 dark:text-emerald-300/80">
          {events[events.length - 1].payDate} &ndash; {events[0].payDate}
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
          {open ? 'Hide' : 'Set each one'}
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && (
        <div className="max-h-[22rem] overflow-y-auto divide-y divide-emerald-300/40 dark:divide-emerald-800/50">
          {events.map((ev) => (
            <DividendPayoutRow
              key={ev.payDate}
              ticker={ticker}
              stockData={stockData}
              totalShares={ev.shares}
              payoutDate={ev.payDate}
              onActioned={onActioned}
              isAllReadOnly={isAllReadOnly}
              actualPayoutsByDate={actualPayoutsByDate}
              compact
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StockBar({ ticker, stockData, positions, onRemovePosition, onSellPosition, onMarkedDrip, onAccountTypeChanged, onBrokerChanged, onStockClick, onRefresh, refreshing, showNews, risk, dismissedPayoutDates, onPayoutActioned, isAllReadOnly = false, actualPayoutsByDate = null, chartLastPayout = null, chartPayouts = null, onMultiSold = null }) {
  const [expanded, setExpanded] = useState(false);
  // Inside the expanded lot list, collapse to the 2 newest lots first;
  // user opts into the full list via "Show More" and the list then
  // overflows into a fixed-height scroller. Mirrors the MyPortfolioTab
  // pattern in the Stock Modal.
  const [showAllLots, setShowAllLots] = useState(false);

  const totalShares = positions.reduce((s, p) => s + (p.shares || 0), 0);
  const totalCost = positions.reduce((s, p) => s + (p.shares || 0) * (p.cost_per_share || 0), 0);
  const avgCost = totalShares > 0 ? totalCost / totalShares : 0;
  const currentValue = totalShares * (stockData?.price || 0);
  const gainLoss = currentValue - totalCost;
  const gainPct = totalCost > 0 ? (gainLoss / totalCost * 100) : 0;
  const isPositive = gainLoss >= 0;
  const divRate = stockData?.dividend_rate || 0;
  const annualIncome = totalShares * divRate;
  const yieldOnCost = totalCost > 0 ? (annualIncome / totalCost * 100) : 0;
  const riskCls = risk ? riskColorClasses(risk.category) : null;
  // Round 97a (Feb 2026) — Non-dividend support in Portfolio rows.
  // Mirror StockTable's adaptive rendering: hide dividend-only badges
  // (King/Aristocrat/Achiever live tier, DividendChangeBadge,
  // TriangleScoreChip, ExDivPill, YoC) and swap the meta line for
  // Sector · Industry · Mkt Cap. Quality badge is muted (no
  // dividend_quality_tier) and the inline Non-Div Quality Tier pill
  // (G-S/A/B/C/D) is shown next to the ticker. Dividend rows are
  // 100% untouched — `pays_dividend !== false` keeps the original
  // path active.
  const isNonDiv = stockData?.pays_dividend === false;
  const fmtMarketCap = (v) => {
    if (v == null) return null;
    const abs = Math.abs(v);
    if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
    if (abs >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
    if (abs >= 1e6)  return `$${(v / 1e6).toFixed(1)}M`;
    if (abs >= 1e3)  return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid={`portfolio-stock-bar-${ticker}`}>
      {/* Stock Header Bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {risk && riskCls && (
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-muted/60 ring-1 ${riskCls.ring} ${riskCls.text} cursor-help`}
                  title={riskTooltip(risk)}
                  data-testid={`portfolio-risk-badge-${ticker}`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${riskCls.dot}`}
                    data-testid={`portfolio-risk-dot-${ticker}`}
                  />
                  {riskLabel(risk.category)} · {risk.score}/10
                </span>
              )}
              <QualityBadge stock={isNonDiv ? { ...(stockData || {}), dividend_quality_score: null, dividend_quality_tier: null } : stockData} size="xs" interactive testIdSuffix={`portfolio-${ticker}`} />
              {/* Round 97a (Feb 2026) — Inline Non-Div Quality Tier pill.
                  Mirrors the pattern from StockTable so non-dividend
                  positions get a tier badge (G-S/A/B/C/D) where their
                  dividend cousins would show a Triangle score. */}
              {isNonDiv && stockData?.non_dividend_quality_tier && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStockClick?.(ticker, 'non-div-growth');
                  }}
                  className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-tight border transition-colors hover:brightness-110 cursor-pointer ${
                    stockData.non_dividend_quality_tier === 'GROWTH-S' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
                    : stockData.non_dividend_quality_tier === 'GROWTH-A' ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60'
                    : stockData.non_dividend_quality_tier === 'GROWTH-B' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-300/60 dark:border-blue-700/60'
                    : stockData.non_dividend_quality_tier === 'GROWTH-C' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60'
                    : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60'
                  }`}
                  title={`Non-div quality tier · score ${stockData.non_dividend_quality_score ?? '—'}/100. Click to view the per-signal breakdown in the Growth tab.`}
                  data-testid={`portfolio-non-div-tier-inline-${ticker}`}
                >
                  {stockData.non_dividend_quality_tier.replace('GROWTH-', 'G-')}
                </button>
              )}
              <TriangleScoreChip stock={isNonDiv ? null : (stockData || { ticker })} />
              <span
                className="font-bold text-sm cursor-pointer hover:text-primary transition-colors"
                onClick={() => onStockClick?.(ticker)}
                data-testid={`portfolio-stock-name-${ticker}`}
              >
                {stockData?.name || ticker}
              </span>
              <span
                className="font-mono text-xs text-yellow-600 dark:text-yellow-400 font-bold cursor-pointer hover:underline"
                onClick={() => onStockClick?.(ticker)}
                data-testid={`portfolio-stock-ticker-${ticker}`}
              >
                {ticker}
              </span>
              {stockData?.dividend_tier === 'king' && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-yellow-100 text-yellow-900 dark:bg-yellow-950/60 dark:text-yellow-200 border border-yellow-300/60 dark:border-yellow-800/60"
                  title="Dividend King — 50+ consecutive years of annual dividend increases"
                  data-testid={`portfolio-tier-badge-king-${ticker}`}
                >
                  <span className="text-[10px] leading-none">👑</span>
                  King
                </span>
              )}
              {stockData?.dividend_tier === 'aristocrat' && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-blue-100 text-blue-900 dark:bg-blue-950/60 dark:text-blue-200 border border-blue-300/60 dark:border-blue-800/60"
                  title="Dividend Aristocrat — S&P 500 + 25+ consecutive years of annual dividend increases"
                  data-testid={`portfolio-tier-badge-aristocrat-${ticker}`}
                >
                  <span className="text-[10px] leading-none">🏆</span>
                  Aristocrat
                </span>
              )}
              {stockData?.dividend_tier === 'achiever' && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-green-100 text-green-900 dark:bg-green-950/60 dark:text-green-200 border border-green-300/60 dark:border-green-800/60"
                  title="Dividend Achiever — 10+ consecutive years of annual dividend increases"
                  data-testid={`portfolio-tier-badge-achiever-${ticker}`}
                >
                  <span className="text-[10px] leading-none">🥉</span>
                  Achiever
                </span>
              )}
              {stockData?.is_leveraged && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border border-red-300/60 dark:border-red-800/60"
                  title="Leveraged / inverse ETF — amplified risk; not suitable for long-term dividend investing"
                  data-testid={`portfolio-leveraged-badge-${ticker}`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" />
                  Leveraged
                </span>
              )}
              {stockData?.uses_futures && !stockData?.is_leveraged && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60"
                  title="Futures-based fund — uses derivatives for exposure. Carries margin/leverage risk and roll costs; returns can diverge from the underlying over time."
                  data-testid={`portfolio-futures-badge-${ticker}`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" />
                  Futures
                </span>
              )}
              {stockData?.is_adr && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 border border-red-300/60 dark:border-red-400/60"
                  title={`American Depositary Receipt — U.S.-listed certificate representing shares of a foreign company. Trades and pays dividends in USD.${stockData?.country_code ? `\n\nCountry of origin: ${ccName(stockData.country_code)}` : ''}`}
                  data-testid={`portfolio-adr-badge-${ticker}`}
                >
                  {stockData?.country_code && (
                    <span className="text-sm leading-none">{ccToFlag(stockData.country_code)}</span>
                  )}
                  ADR
                </span>
              )}
              {stockData?.is_bdc && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60"
                  title="Business Development Company — closed-end fund that loans to small/mid-sized private businesses. By law, distributes 90%+ of taxable income; yields of 8-12% are normal."
                  data-testid={`portfolio-bdc-badge-${ticker}`}
                >
                  BDC
                </span>
              )}
              {stockData?.is_cef && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-300/60 dark:border-teal-700/60"
                  title="Closed-End Fund — exchange-traded fund with a fixed share count that can trade at a premium or discount to NAV. Often uses leverage; many distribute monthly with high yields."
                  data-testid={`portfolio-cef-badge-${ticker}`}
                >
                  CEF
                </span>
              )}
              <LeveragedCefIcon
                ticker={ticker}
                isCef={!!stockData?.is_cef}
                testIdPrefix="portfolio-lev-cef-badge"
              />
              {/* Round P140·5 (Feb 2026) — Warrant badge in the
                  portfolio row so users see WARRANT / REDEEM SOON /
                  REDEEMED alongside the ticker without opening the
                  modal. Fresh 8-K redemption events flip the state
                  automatically via the weekly watcher. */}
              {stockData?.is_warrant && (
                <WarrantBadge
                  status={stockData?.warrant_status || 'active'}
                  deadline={stockData?.warrant_redemption_deadline}
                  price={stockData?.warrant_redemption_price}
                  isUnit={!!stockData?.is_unit}
                  testIdSuffix={`portfolio-row-${ticker}`}
                />
              )}
              {stockData?.is_reit && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-700/60"
                  title="Real Estate Investment Trust — company that owns, operates, or finances income-producing real estate. Required by law to distribute 90%+ of taxable income, so yields of 4-8% are typical."
                  data-testid={`portfolio-reit-badge-${ticker}`}
                >
                  REIT
                </span>
              )}
              <DividendChangeBadge stock={isNonDiv ? null : (stockData || { ticker })} />
              {stockData && <PreferredCallRiskChip stock={stockData} />}
            </div>
            {stockData?.price != null && (
              <div
                className="flex items-center gap-1.5 mt-0.5 text-xs flex-wrap"
                data-testid={`portfolio-current-price-row-${ticker}`}
              >
                <span className="text-muted-foreground whitespace-nowrap">Current Price:</span>
                <span
                  className="font-mono font-semibold text-foreground whitespace-nowrap"
                  data-testid={`portfolio-current-price-${ticker}`}
                >
                  ${Number(stockData.price).toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-center gap-x-3 gap-y-1 mt-0.5 text-xs text-muted-foreground flex-wrap">
              <span className="whitespace-nowrap">{totalShares.toFixed(2)} shares</span>
              <span className="whitespace-nowrap">Avg ${avgCost.toFixed(2)}</span>
              <span className="font-mono whitespace-nowrap">
                <span className="font-normal text-muted-foreground mr-1">Value:</span>
                ${currentValue.toFixed(2)}
              </span>
              <span className={`font-mono font-medium whitespace-nowrap ${isPositive ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <span className="font-normal text-muted-foreground mr-1">Return:</span>
                {isPositive ? '+' : ''}{gainPct.toFixed(1)}%
              </span>
              <span className={`font-mono font-medium whitespace-nowrap ${isPositive ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <span className="font-normal text-muted-foreground mr-1">Gain/Loss:</span>
                {isPositive ? '+' : ''}${gainLoss.toFixed(2)}
              </span>
              {yieldOnCost > 0 && !isNonDiv && (
                <span className="font-mono font-medium text-emerald-700 dark:text-emerald-400 whitespace-nowrap" title="Yield on Cost: annual dividend income ÷ what you paid">
                  <span className="font-normal text-muted-foreground mr-1">YoC:</span>
                  {yieldOnCost.toFixed(2)}%
                </span>
              )}
              {!isNonDiv && <ExDivPill stockData={stockData} />}
            </div>
            {/* Round 97a (Feb 2026) — Non-dividend meta line replaces
                the YoC/ExDiv slot with Sector · Industry · Mkt Cap so
                non-div positions still convey at-a-glance context. */}
            {isNonDiv && (
              <div
                className="flex items-center gap-x-3 gap-y-0 mt-0.5 text-[11px] text-muted-foreground flex-wrap"
                data-testid={`portfolio-nondiv-meta-${ticker}`}
              >
                {stockData?.sector && (
                  <span className="whitespace-nowrap" data-testid={`portfolio-nondiv-sector-${ticker}`}>
                    <span className="font-normal mr-1">Sector:</span>
                    <span className="text-foreground font-medium">{stockData.sector}</span>
                  </span>
                )}
                {stockData?.industry && (
                  <span className="whitespace-nowrap" data-testid={`portfolio-nondiv-industry-${ticker}`}>
                    <span className="font-normal mr-1">Industry:</span>
                    <span className="text-foreground font-medium">{stockData.industry}</span>
                  </span>
                )}
                {stockData?.market_cap != null && (
                  <span className="whitespace-nowrap font-mono" data-testid={`portfolio-nondiv-mcap-${ticker}`}>
                    <span className="font-normal mr-1 font-sans">Mkt Cap:</span>
                    <span className="text-foreground font-medium">{fmtMarketCap(stockData.market_cap)}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md border text-[10px] font-bold bg-muted hover:bg-muted/80 transition-colors text-foreground leading-tight"
            data-testid={`portfolio-toggle-${ticker}`}
            title={expanded ? 'Hide share lots' : 'Show share lots'}
          >
            <span className="flex flex-col items-center">
              <span>{expanded ? 'Hide' : 'Show'}</span>
              <span>Shares</span>
            </span>
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Dividend payout reminder — shows above positions only when the
          ticker paid out within the last 14 days AND the user hasn't
          actioned or dismissed that specific payout. Always visible
          (not gated on the expanded-positions toggle) so users don't
          miss the reminder just because they collapsed the stock.

          Round 96 (Jul 2026) — Source-of-truth swap. Historically we
          gated on `stockData.last_payment_date`, but that field is
          maintained by an async cache refresh that can lag by days
          (EGGY: DB showed May 28 for a week after the Jun 29 pay).
          The Portfolio Value chart already draws the correct dot
          because it reads live Massive pay events via
          /api/portfolio/value-history. We now piggy-back on that same
          source (`chartLastPayout`) and only fall back to
          `stockData.last_payment_date` when the chart source has no
          past event for the ticker (brand-new positions inside their
          first payout cycle). */}
      {(() => {
        const toIso = (raw) => _toIsoDate(raw);
        const todayIso = _toIsoDate(new Date());
        const freq = stockData?.frequency;
        const tol = payoutMatchTolerance(freq);
        const windowDays = payoutWindowDays(freq);
        // Entitled shares as of a given cutoff (ex-date preferred, else
        // pay date). Round 95h — lots bought on/after the ex-date never
        // earned that payment, and prompting on them corrupts the ledger.
        const sharesEntitled = (cutoffIso) => positions.reduce((s, p) => {
          const dp = p.date_purchased ? String(p.date_purchased).slice(0, 10) : null;
          if (cutoffIso && dp && dp >= cutoffIso) return s;
          return s + (p.shares || 0);
        }, 0);

        // Every past pay event the value-history feed knows about, newest
        // first. Falls back to the single cached last_payment_date for a
        // brand-new position inside its first cycle.
        const feed = Array.isArray(chartPayouts) && chartPayouts.length > 0
          ? chartPayouts
          : (chartLastPayout ? [chartLastPayout] : []);
        let candidates = feed.length > 0 ? feed : (
          stockData?.last_payment_date
            ? [{ payDate: stockData.last_payment_date, exDate: stockData?.ex_dividend_date, amount: 0, shares: 0 }]
            : []
        );

        const events = [];
        for (const ev of candidates) {
          const payIso = toIso(ev.payDate);
          if (!payIso || !isRecentPayoutWindow(payIso, windowDays)) continue;
          // Sep 2026 — skip a payout dated TODAY. It settles T+1 and
          // still shows as pending at the broker, so prompting the user
          // to reinvest cash that hasn't landed invites a wrong entry.
          // It shows up tomorrow.
          if (payIso >= todayIso) continue;
          if (isPayoutDismissed(dismissedPayoutDates, payIso, tol)) continue;
          const exIso = (() => {
            const e = toIso(ev.exDate);
            if (e && e <= payIso) return e;
            const cached = toIso(stockData?.ex_dividend_date);
            return cached && cached <= payIso ? cached : null;
          })();
          // Prefer the feed's shares-held-on-ex figure; recompute only
          // when the feed didn't ship one.
          const shares = Number(ev.shares) > 0 ? Number(ev.shares) : sharesEntitled(exIso || payIso);
          if (!(shares > 0)) continue;
          events.push({ payDate: payIso, exDate: exIso, amount: Number(ev.amount) || 0, shares });
        }
        if (events.length === 0) return null;
        events.sort((a, b) => (a.payDate < b.payDate ? 1 : -1));

        // One amount map for every event in the queue, so each row shows
        // the exact $ that payout paid rather than an estimate.
        const mergedActuals = (() => {
          const base = { ...(actualPayoutsByDate || {}) };
          for (const ev of events) {
            if (ev.amount > 0 && !(Number(base[ev.payDate]) > 0)) base[ev.payDate] = ev.amount;
          }
          return base;
        })();

        return (
          <DividendPayoutQueue
            ticker={ticker}
            stockData={stockData}
            events={events}
            onActioned={onPayoutActioned}
            isAllReadOnly={isAllReadOnly}
            actualPayoutsByDate={mergedActuals}
          />
        );
      })()}

      {/* Positions List — newest purchase first by default. Two lots are
          shown until the user clicks "Show More"; from then on the rest
          slide out and the whole stack scrolls inside a fixed max-height,
          mirroring the pattern used in the Stock Modal's "My Portfolio"
          tab. Click "Show Less" to collapse back to two. */}
      {expanded && (() => {
        const sorted = [...positions].sort((a, b) => {
          const ad = new Date(a.date_purchased || 0).getTime();
          const bd = new Date(b.date_purchased || 0).getTime();
          if (bd !== ad) return bd - ad; // newest first
          // tiebreak on position_id so the order stays stable across renders
          return String(b.position_id || '').localeCompare(String(a.position_id || ''));
        });
        const COLLAPSED_COUNT = 2;
        const showMoreButton = sorted.length > COLLAPSED_COUNT;
        const visible = showAllLots ? sorted : sorted.slice(0, COLLAPSED_COUNT);
        const hiddenCount = sorted.length - COLLAPSED_COUNT;
        return (
          <>
            {/* P95 (Jun 2026) — Single Multi-Share Sell: the top row of the
                lot list. Sell N shares across multiple lots in one action
                using FIFO/LIFO/HIFO/LOFO/Optimizer. Hidden in the read-only
                All-Portfolios view (writes need a concrete portfolio). */}
            {!isAllReadOnly && totalShares > 0 && (
              <SingleMultiShareSell
                ticker={ticker}
                totalShares={totalShares}
                currentPrice={stockData?.price}
                onSold={onMultiSold}
              />
            )}
            <div
              className={showAllLots ? 'max-h-[256px] overflow-y-auto' : ''}
              data-testid={`portfolio-positions-scroller-${ticker}`}
            >
              {visible.map(pos => (
                <PositionRow
                  key={pos.position_id}
                  position={pos}
                  onRemove={onRemovePosition}
                  onSell={onSellPosition}
                  onMarkedDrip={onMarkedDrip}
                  onAccountTypeChanged={onAccountTypeChanged}
                  onBrokerChanged={onBrokerChanged}
                  ticker={ticker}
                  stockPrice={stockData?.price}
                  isAllReadOnly={isAllReadOnly}
                  isNonDiv={isNonDiv}
                />
              ))}
            </div>
            {showMoreButton && (
              <button
                type="button"
                onClick={() => setShowAllLots(v => !v)}
                className="w-full flex items-center justify-center gap-1 px-3 py-1.5 border-t border-border/50 text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                data-testid={`portfolio-positions-more-toggle-${ticker}`}
                title={showAllLots
                  ? 'Collapse back to the two newest lots'
                  : `Show the other ${hiddenCount} lot${hiddenCount === 1 ? '' : 's'}`}
              >
                {showAllLots ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Show Less
                    <ChevronUp className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Show More ({hiddenCount})
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
            )}
          </>
        );
      })()}

      {/* Inline News */}
      {showNews && <NewsInline ticker={ticker} />}
    </div>
  );
}

export default function PortfolioView({ ownedTickers, stocks, onPositionsChanged, onStockClick, showNews, onNewsToggle }) {
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus tier All view is
  // a summary; hide per-row Sell / Remove / DRIP toggle / dividend
  // payout buttons because writes against the "all" bucket don't
  // belong to any one portfolio.
  const { isReadOnly: isAllReadOnly } = useIsReadOnlyAllView();
  const [allPositions, setAllPositions] = useState({});
  const [stockDataMap, setStockDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [riskData, setRiskData] = useState(null);
  // Round P136 (Feb 2026) — leveraged-CEF set for the concentration
  // banner rendered below the grid. Shared hook caches across pages
  // so opening the Portfolio tab doesn't re-fetch.
  const leveragedCefSet = useLeveragedCefs();
  // Round 92o++++++++N++++++++++ (Jun 2026) — accurate dividend-payout
  // banner amounts. Previously the banner used a rough estimate
  // (current_shares × dividend_rate / freq) which over/under-stated the
  // actual payout when (a) the user bought more shares AFTER the
  // ex-div date, or (b) the ETF is a variable-distribution fund where
  // `dividend_rate / freq` doesn't match the latest per-share amount.
  // Fix: read the actual per-ticker amount from /portfolio/dividend-
  // calendar (already scoped by portfolio_id) and pass it down to the
  // banner. The calendar's `amount` field uses shares-at-ex-div × per
  // share, so it matches what the user actually received.
  const [lastPayoutByTicker, setLastPayoutByTicker] = useState({});
  // Round 96 (Jul 2026) — Chart-source dividend map. Same data used by
  // the Portfolio Value Chart's dividend dot (via
  // /api/portfolio/value-history), which reads live from the Massive
  // dividends endpoint. Keyed by ticker → the most recent PAST payout
  // event { payDate, exDate, amount, perShare, shares }. Used by the
  // DividendPayoutRow banner as its primary source of truth because
  // `stockData.last_payment_date` can lag by days when the cache
  // hasn't been refreshed after a fresh payment (regression that
  // silently killed the "You received a dividend" banner for EGGY).
  const [chartLastPayoutByTicker, setChartLastPayoutByTicker] = useState({});
  // Sep 2026 — every past pay event per ticker (newest first), from the
  // same value-history feed. Feeds the payout QUEUE so a daily payer's
  // un-actioned days can all be listed and set individually.
  const [chartPayoutsByTicker, setChartPayoutsByTicker] = useState({});

  // Round 96j (Jul 2026) — Portfolio row sort state. Persisted to
  // localStorage keyed by `portfolio-sort-v1`. Sorts by any of the
  // 8 chips in the sort bar (name / shares / purchased / value /
  // ex-div / div pay / return% / gain-loss). Hooks live here (top
  // of component) so they satisfy rules-of-hooks even when the
  // early-return for "no positions" fires further down.
  const PORT_SORT_KEYS = ['name', 'shares', 'purchased', 'value', 'ex_div', 'div_pay', 'return_pct', 'gain_loss'];
  const [portSortKey, _setPortSortKey] = useState(() => {
    try {
      const raw = localStorage.getItem('portfolio-sort-v1');
      const parsed = raw ? JSON.parse(raw) : null;
      return (parsed?.key && PORT_SORT_KEYS.includes(parsed.key)) ? parsed.key : 'name';
    } catch { return 'name'; }
  });
  const [portSortDir, _setPortSortDir] = useState(() => {
    try {
      const raw = localStorage.getItem('portfolio-sort-v1');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.dir === 'desc' ? 'desc' : 'asc';
    } catch { return 'asc'; }
  });
  const persistPortSort = (k, d) => {
    try { localStorage.setItem('portfolio-sort-v1', JSON.stringify({ key: k, dir: d })); } catch {}
  };
  const onPortSortClick = (key) => {
    if (portSortKey === key) {
      const nd = portSortDir === 'asc' ? 'desc' : 'asc';
      _setPortSortDir(nd);
      persistPortSort(key, nd);
    } else {
      const descFirst = new Set(['shares', 'value', 'return_pct', 'gain_loss']);
      const nd = descFirst.has(key) ? 'desc' : 'asc';
      _setPortSortKey(key);
      _setPortSortDir(nd);
      persistPortSort(key, nd);
    }
  };
  // Map of ticker → Set of dismissed/actioned `payout_date` strings so
  // the DividendPayoutRow knows what to hide. Refetched on initial
  // mount and again after every successful action inside a payout row.
  const [dismissedMap, setDismissedMap] = useState({});
  const refreshDismissals = async () => {
    try {
      const res = await axios.get(`${API}/portfolio/dividend-payout/dismissals`, { withCredentials: true });
      const map = {};
      (res.data?.dismissals || []).forEach((d) => {
        if (!map[d.ticker]) map[d.ticker] = new Set();
        // Prefer the backend's ISO form; fall back to client-side
        // normalization for older records that pre-dated the schema bump.
        const iso = d.payout_date_iso || _toIsoDate(d.payout_date);
        if (iso) map[d.ticker].add(iso);
      });
      setDismissedMap(map);
    } catch {
      // Non-fatal; the worst case is a dismissed row re-appears until next load.
    }
  };
  useEffect(() => { refreshDismissals(); }, []);
  // When a payout row fires an action (add-to-cash / drip-buy / dismiss)
  // we refresh the dismissals list AND bump the parent so cash balance
  // and positions re-sync. DRIP buys create new lots so positions need
  // a refetch; add-to-cash mutates the users table so the cash summary
  // up-top needs to refresh.
  const handlePayoutActioned = () => {
    refreshDismissals();
    onPositionsChanged?.();
    setPriceRefreshTick((t) => t + 1);
  };
  const userLimits = useUserLimits();
  // Bumped when a background refresh finishes so we re-fetch fresh prices.
  const [priceRefreshTick, setPriceRefreshTick] = useState(0);
  // Tracks which tickers have an in-flight force-refresh so we can show
  // the spinner on the per-stock refresh button.
  const [refreshingTickers, setRefreshingTickers] = useState(() => new Set());
  // refreshingAll state + handleRefreshAll removed 2026-05-02.

  // Fetch positions (separate from stock prices so price refreshes don't
  // re-fetch positions on every background batch event).
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio:
  // scope the per-ticker lots to the active portfolio_id so that ALL
  // money/share aggregates inside StockBar (totalShares, totalCost, the
  // dividend payout banner, the Tax Lots rollup, DRIP toggles) only
  // touch lots belonging to the visible portfolio. Without this, every
  // scoped view rendered the user-wide lot totals.
  const posScopeParams = usePortfolioScopeParams();
  const posScopeKey = posScopeParams.portfolio_id || 'all';
  // Round P124 (Feb 2026) — Portfolio-page load-order gate. Stage
  // 4 unlocks all middle-of-page fetches (positions, batch prices,
  // risk-score, dividend-calendar, chart-source dividend fetch).
  // Advances to stage 5 the moment these fire so the Portfolio
  // Value chart card can finally light up its own slow endpoints.
  const pvLoadStage = usePortfolioLoadStage();
  const pvActive = pvLoadStage >= 4;
  // Round P124c — stage 5 is now driven by the time-based
  // staircase in portfolioLoadOrder.js. No fetch-completion
  // advance needed here.
  useEffect(() => {
    if (!pvActive) return undefined;
    if (!ownedTickers || ownedTickers.length === 0) {
      // No owned tickers — clear stale positions/data so the empty-state
      // renders immediately after Reset/Delete instead of showing old rows.
      setAllPositions({});
      setStockDataMap({});
      setLoading(false);
      return;
    }
    setLoading(true);

    // Round P125 (Feb 2026) — Single bulk call replaces the
    // per-ticker Promise.all loop. 20 owned tickers = 1 HTTP round-
    // trip instead of 20, so the positions grid unblocks in a
    // single hop past the browser's 6-concurrent HTTP cap.
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/portfolio/positions/bulk`, {
          params: { ...posScopeParams, tickers: ownedTickers.join(',') },
          withCredentials: true,
        });
        const raw = res.data || {};
        const posMap = {};
        ownedTickers.forEach((t) => { posMap[t] = Array.isArray(raw[t]) ? raw[t] : []; });
        setAllPositions(posMap);
      } catch (_e) {
        // Never leave the grid stuck in an inconsistent partial state.
        const empty = {};
        ownedTickers.forEach((t) => { empty[t] = []; });
        setAllPositions(empty);
      } finally {
        setLoading(false);
      }
    };
    fetchPositions();
    return undefined;
  }, [ownedTickers, posScopeKey, priceRefreshTick, pvActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always fetch CURRENT prices for every owned ticker via /stocks/batch
  // (5-min server cache), re-run on background-refresh events so prices
  // are live whenever the user lands on / returns to the portfolio page.
  useEffect(() => {
    if (!pvActive) return undefined;
    if (!ownedTickers || ownedTickers.length === 0) return undefined;
    let cancelled = false;
    const fetchPrices = async () => {
      // Backend caps /stocks/batch at 20 tickers per call — chunk defensively.
      const chunks = [];
      for (let i = 0; i < ownedTickers.length; i += 20) {
        chunks.push(ownedTickers.slice(i, i + 20));
      }
      const map = {};
      for (const chunk of chunks) {
        if (cancelled) return;
        try {
          const res = await axios.get(`${API}/stocks/batch/${chunk.join(',')}`, { withCredentials: true });
          (res.data || []).forEach(s => { if (s?.ticker) map[s.ticker] = s; });
        } catch { /* ignore chunk failure, keep any prior data */ }
      }
      if (!cancelled) setStockDataMap(prev => ({ ...prev, ...map }));
    };
    fetchPrices();
    return () => { cancelled = true; };
  }, [ownedTickers, priceRefreshTick, pvActive]);

  // Round P112f (Jul 31, 2026) — Cadence Confirmed toast.
  // When Massive posts a 2nd payment for a ticker that was previously
  // flagged `frequency_inferred_thin`, the row auto-promotes off the
  // thin flag (via the spacing-heuristic in `_load_massive_div_metrics_sync`).
  // Detect that transition on the client so a holder gets a quiet
  // celebration toast when their warning is retired. Guarded per-ticker
  // via localStorage so we only fire once per confirmation event.
  useEffect(() => {
    if (!ownedTickers || ownedTickers.length === 0) return;
    ownedTickers.forEach((t) => {
      const s = stockDataMap[t] || stocks.find(x => x.ticker === t);
      if (!s) return;
      const seenAsThinKey = `dividrip.thin.seen.${t}`;
      const confirmedToastKey = `dividrip.thin.confirmed.${t}`;
      try {
        if (s.frequency_inferred_thin) {
          // Mark as having been observed thin so the confirmation
          // transition can fire later.
          localStorage.setItem(seenAsThinKey, '1');
        } else if (
          localStorage.getItem(seenAsThinKey) === '1' &&
          localStorage.getItem(confirmedToastKey) !== '1'
        ) {
          // Thin → confirmed transition. Celebrate once, then latch.
          toast.success(`${t}: dividend cadence confirmed`, {
            description: `A second payment posted. ${t} now shows ${s.frequency || 'a confirmed frequency'} — projected income is reliable.`,
            duration: 6500,
          });
          localStorage.setItem(confirmedToastKey, '1');
          // Also clear any dismissed-thin flag so the banner logic
          // treats this ticker as fresh state.
          localStorage.removeItem(`dividrip.thin.dismissed.${t}`);
        }
      } catch { /* localStorage disabled — silent skip */ }
    });
  }, [stockDataMap, ownedTickers, stocks]);


  // Fetch the per-position Income Risk Score + portfolio concentration
  // banner data. Cheap (~20 ms server-side) — re-fetches whenever the
  // ticker list changes or after a price refresh (which may have
  // updated payout_ratio / yield).
  const riskScopeParams = usePortfolioScopeParams();
  const riskScopeKey = riskScopeParams.portfolio_id || 'all';
  const swrRiskTick = useSwrTick('/portfolio/risk-score');
  const swrValueTick = useSwrTick('/portfolio/value-history');
  useEffect(() => {
    if (!pvActive) return undefined;
    if (!ownedTickers || ownedTickers.length === 0) {
      setRiskData(null);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${API}/portfolio/risk-score`, { params: riskScopeParams, withCredentials: true })
      .then((res) => { if (!cancelled) setRiskData(res.data); })
      .catch(() => { if (!cancelled) setRiskData(null); });
    return () => { cancelled = true; };
  }, [ownedTickers, priceRefreshTick, riskScopeKey, pvActive, swrRiskTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the scoped dividend calendar so the per-row payout banner can
  // show the ACTUAL amount received (instead of a current-shares × rate
  // estimate that misses post-ex-div lot additions and variable-distrib
  // ETFs). Re-runs when the active portfolio changes.
  //
  // BUGFIX (Jun 2026): keying this map by `ticker → { amount, date }`
  // and "latest date wins" silently picked up FUTURE projected pay
  // events (e.g. EGGY has projected pay_dates through Dec 2026), so
  // the lookup never matched the actual displayed `payoutDate` ("May
  // 29, 2026") and the row fell back to the rough estimator — which
  // is exactly the bug we were trying to fix. Index every pay event
  // explicitly by date so the row can look up the exact payout.
  useEffect(() => {
    if (!pvActive) return undefined;
    if (!ownedTickers || ownedTickers.length === 0) {
      setLastPayoutByTicker({});
      return undefined;
    }
    let cancelled = false;
    const yr = new Date().getFullYear();
    axios.get(`${API}/portfolio/dividend-calendar`, {
      params: { year: yr, include_watchlist: false, ...riskScopeParams },
      withCredentials: true,
    })
      .then((res) => {
        if (cancelled) return;
        const events = res.data?.events || [];
        // ticker → { ISOdate → amount } so the row can pick the exact
        // event for the date the banner displays.
        const byTicker = {};
        for (const e of events) {
          if (e.type !== 'pay_date') continue;
          const t = (e.ticker || '').toUpperCase();
          if (!t || !e.date) continue;
          const amt = Number(e.amount || 0);
          if (!(amt > 0)) continue;
          if (!byTicker[t]) byTicker[t] = {};
          byTicker[t][e.date] = amt;
        }
        setLastPayoutByTicker(byTicker);
      })
      .catch(() => { if (!cancelled) setLastPayoutByTicker({}); });
    return () => { cancelled = true; };
  }, [ownedTickers, priceRefreshTick, riskScopeKey, pvActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Round 96 (Jul 2026) — Chart-source dividend fetch. Piggy-backs on the
  // same endpoint the Portfolio Value Chart uses to render its dividend
  // dot (/api/portfolio/value-history), which pulls live pay events
  // from Massive. We take the most recent PAST pay event per ticker so
  // the DividendPayoutRow banner can gate on the *actual* pay_date
  // (not on the stock_cache mirror that lags by days after a payment).
  //
  // 3m window is more than enough — the banner only fires within 14
  // days of pay_date and dropping older payouts avoids picking up
  // stale events that were already dismissed months ago.
  useEffect(() => {
    if (!pvActive) return undefined;
    if (!ownedTickers || ownedTickers.length === 0) {
      setChartLastPayoutByTicker({});
      return undefined;
    }
    let cancelled = false;
    axios.get(`${API}/portfolio/value-history`, {
      params: { period: '3m', ...riskScopeParams },
      withCredentials: true,
    })
      .then((res) => {
        if (cancelled) return;
        const events = res.data?.dividends || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const byT = {};
        const allByT = {};
        for (const e of events) {
          const t = String(e.ticker || '').toUpperCase();
          const payIso = e.pay_date || e.date;
          if (!t || !payIso) continue;
          const pd = new Date(payIso + 'T00:00:00');
          if (isNaN(pd.getTime()) || pd > today) continue; // ignore future/projected
          const row = {
            payDate: payIso,
            exDate: e.ex_date || null,
            amount: Number(e.amount || 0),
            perShare: Number(e.per_share || 0),
            shares: Number(e.shares || 0),
          };
          // Sep 2026 — keep EVERY past event, not just the newest. A
          // daily payer pays every business day and the payout queue
          // has to be able to list all of the un-actioned ones.
          if (!allByT[t]) allByT[t] = [];
          allByT[t].push(row);
          const cur = byT[t];
          if (!cur || payIso > cur.payDate) byT[t] = row;
        }
        for (const t of Object.keys(allByT)) {
          allByT[t].sort((a, b) => (a.payDate < b.payDate ? 1 : -1));
        }
        setChartLastPayoutByTicker(byT);
        setChartPayoutsByTicker(allByT);
      })
      .catch(() => {
        if (!cancelled) {
          setChartLastPayoutByTicker({});
          setChartPayoutsByTicker({});
        }
      });
    return () => { cancelled = true; };
  }, [ownedTickers, priceRefreshTick, riskScopeKey, pvActive, swrValueTick]); // eslint-disable-line react-hooks/exhaustive-deps
  // the refresh tick so prices re-load from cache once the server has
  // pulled fresh data.
  //
  // Round 95bu (Jun 2026) — Removed the `visibilitychange` + `focus`
  // listeners that used to call `bump()` whenever the user tabbed back
  // to the site. Reason: those listeners cause the whole positions
  // list to refetch, which collapses any expanded "show shares" panel
  // the user had open and scrolls them off whatever row they were
  // reading. We don't need them — live prices already arrive
  // continuously via the WS feed + SSE 3-second REST overlay, and any
  // actual portfolio mutation still fires `portfolio-changed` from the
  // mutating code path. The visibility/focus refresh was leftover
  // pre-live-feed scaffolding.
  useEffect(() => {
    let pending = null;
    const bump = () => {
      // Debounce: coalesce rapid-fire batch events into one refetch/sec.
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        setPriceRefreshTick(t => t + 1);
      }, 1000);
    };
    window.addEventListener('tracked-refresh-progress', bump);
    window.addEventListener('portfolio-changed', bump);
    return () => {
      if (pending) clearTimeout(pending);
      window.removeEventListener('tracked-refresh-progress', bump);
      window.removeEventListener('portfolio-changed', bump);
    };
  }, []);

  const handleRemove = async (ticker, positionId) => {
    try {
      await axios.delete(`${API}/portfolio/${ticker}/positions/${positionId}`, { withCredentials: true });
      setAllPositions(prev => {
        const updated = { ...prev };
        updated[ticker] = (updated[ticker] || []).filter(p => p.position_id !== positionId);
        if (updated[ticker].length === 0) delete updated[ticker];
        return updated;
      });
      onPositionsChanged?.();
    } catch {}
  };

  // Partial sell with sale price + date. Proceeds added to user's cash.
  const handleSell = async (ticker, positionId, { shares, sale_price, sale_date }) => {
    const { data } = await axios.post(
      `${API}/portfolio/${ticker}/positions/${positionId}/sell`,
      { shares, sale_price, sale_date },
      { withCredentials: true }
    );
    setAllPositions(prev => {
      const updated = { ...prev };
      if (data.position) {
        updated[ticker] = (updated[ticker] || []).map(p => p.position_id === positionId ? data.position : p);
      } else {
        updated[ticker] = (updated[ticker] || []).filter(p => p.position_id !== positionId);
        if (updated[ticker].length === 0) delete updated[ticker];
      }
      return updated;
    });
    onPositionsChanged?.();
    window.dispatchEvent(new Event('cash-changed'));
    return data;
  };

  // P95 (Jun 2026) — after a Multi-Share Sell executes, the affected
  // ticker's lots changed server-side (decrements/deletes across many
  // lots), so refetch just that ticker's positions instead of trying to
  // splice the consumption plan locally. Cash already updated via the
  // 'cash-changed' event the widget dispatches.
  const handleMultiSold = async (ticker) => {
    try {
      const res = await axios.get(`${API}/portfolio/${ticker}/positions`, { params: posScopeParams, withCredentials: true });
      setAllPositions(prev => {
        const updated = { ...prev };
        if ((res.data || []).length === 0) delete updated[ticker];
        else updated[ticker] = res.data;
        return updated;
      });
    } catch {}
    onPositionsChanged?.();
  };

  // Retroactive DRIP-mark callback wired from PositionRow → StockBar →
  // here. We splice the server-returned position back into the local
  // map so the icon flips from hollow to solid without a full refetch.
  const handleMarkedDrip = (ticker, positionId, updatedPosition) => {
    if (!updatedPosition) return;
    setAllPositions(prev => {
      const next = { ...prev };
      next[ticker] = (next[ticker] || []).map(p =>
        p.position_id === positionId ? { ...p, ...updatedPosition } : p
      );
      return next;
    });
    onPositionsChanged?.();
  };

  // Account-type change callback. For a single-lot edit, only that
  // position is updated. For an apply-all bulk edit, every lot for the
  // ticker gets the new value in local state so sibling rows reflect
  // the change without a full refetch. For an apply-portfolio edit,
  // every lot across every ticker flips locally.
  const handleAccountTypeChanged = (ticker, positionId, newType, opts = {}) => {
    setAllPositions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(tk => {
        next[tk] = (next[tk] || []).map(p => {
          if (opts.applyPortfolio) {
            return { ...p, account_type: newType };
          }
          if (tk === ticker && (opts.applyAll || p.position_id === positionId)) {
            return { ...p, account_type: newType };
          }
          return p;
        });
      });
      return next;
    });
    onPositionsChanged?.();
  };

  // Broker change callback — same shape as handleAccountTypeChanged
  // but flips `broker` (which can be null = Untagged) on the matching
  // lots locally so the UI reflects without a full refetch.
  const handleBrokerChanged = (ticker, positionId, newBroker, opts = {}) => {
    setAllPositions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(tk => {
        next[tk] = (next[tk] || []).map(p => {
          if (opts.applyPortfolio) {
            return { ...p, broker: newBroker };
          }
          if (tk === ticker && (opts.applyAll || p.position_id === positionId)) {
            return { ...p, broker: newBroker };
          }
          return p;
        });
      });
      return next;
    });
    onPositionsChanged?.();
  };

  // Per-stock force refresh: POST to /stocks/{ticker}/refresh (bypasses
  // the 60s + 1h cache windows but keeps Massive background
  // enrichment throttled server-side to once per minute).
  const handleRefresh = async (ticker) => {
    if (refreshingTickers.has(ticker)) return;
    setRefreshingTickers(prev => {
      const next = new Set(prev);
      next.add(ticker);
      return next;
    });
    try {
      const { data } = await axios.post(
        `${API}/stocks/${ticker}/refresh`,
        {},
        { withCredentials: true }
      );
      if (data && data.ticker) {
        setStockDataMap(prev => ({ ...prev, [data.ticker]: data }));
      }
    } catch {
      /* ignore — cache stays as-is */
    } finally {
      setRefreshingTickers(prev => {
        const next = new Set(prev);
        next.delete(ticker);
        return next;
      });
    }
  };

  // handleRefreshAll removed 2026-05-02 — was the "Refresh All" button
  // that hit /stocks/{t}/refresh for every position and cascaded into
  // bulk Massive enrichment. Per-stock RefreshCw button on each
  // position row remains (rate-limited server-side to once per 5 min).

  // Aggregate news vibe across the entire portfolio. Only fires when
  // `showNews` is on so we don't pre-emptively warm news for users
  // who never expand it. Each NewsInline already calls /news for its
  // ticker; this effect makes the same calls but they hit the
  // server-side news_cache (24h TTL) so the second-pass cost is just
  // a Mongo read per ticker (~30ms ea, parallel).
  const [allArticles, setAllArticles] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!showNews) {
      setAllArticles([]);
      return undefined;
    }
    const tickers = Object.keys(allPositions).filter(t => allPositions[t]?.length > 0);
    if (tickers.length === 0) return undefined;
    (async () => {
      try {
        const results = await Promise.all(
          tickers.map(t =>
            axios.get(`${API}/stocks/${t}/news`)
              .then(res => res.data?.articles || [])
              .catch(() => [])
          )
        );
        if (cancelled) return;
        const flat = results.flatMap((arr, i) => arr.map(a => ({ ...a, _ticker: tickers[i] })));
        setAllArticles(flat);
      } catch {
        if (!cancelled) setAllArticles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [showNews, allPositions]);

  // Round P158 fix — only show the full-page spinner on the FIRST
  // load (no rows yet). Refetches (adds/sells firing the
  // 'portfolio-changed' event) keep the tree mounted so modals
  // rendered inside PortfolioView (Quick Stock Add) aren't
  // unmounted/closed mid-flow; stale rows persist ~1s until fresh
  // data lands.
  if (loading && Object.keys(allPositions).length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeTickers = Object.keys(allPositions).filter(t => allPositions[t]?.length > 0);

  // Round 96j (Jul 2026) — Derived sorted ticker list. Sort state
  // hooks live at the top of the component (see `portSortKey` /
  // `portSortDir` declarations near the other hooks) so React's
  // rules-of-hooks stay satisfied even when the early-return above
  // fires. Sort logic itself is a plain sync computation so it can
  // safely live below the early return.
  const sortedActiveTickers = (() => {
    const rows = activeTickers.map((t) => {
      const positions = allPositions[t] || [];
      const stockData = stockDataMap[t] || stocks.find((s) => s.ticker === t) || {};
      const price = Number(stockData?.price ?? 0);
      const shares = positions.reduce((s, p) => s + Number(p?.shares || 0), 0);
      const costBasis = positions.reduce((s, p) => s + Number(p?.shares || 0) * Number(p?.cost_per_share || 0), 0);
      const value = shares * price;
      const gainLoss = value - costBasis;
      const returnPct = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0;
      const parseDate = (raw) => {
        if (!raw) return null;
        const iso = String(raw).length === 10 && String(raw)[4] === '-' ? new Date(raw + 'T00:00:00') : new Date(raw);
        return isNaN(iso.getTime()) ? null : iso.getTime();
      };
      const purchased = positions.reduce((min, p) => {
        const t2 = parseDate(p?.date_purchased);
        if (t2 == null) return min;
        return min == null || t2 < min ? t2 : min;
      }, null);
      return {
        ticker: t,
        name: (stockData?.name || t).toLowerCase(),
        shares,
        purchased: purchased ?? -Infinity,
        value,
        // Round 96l (Jul 2026) — user reported ex-div sort was
        // stuck. The stock_cache field `ex_dividend_date` stores
        // the LAST ex-div (already past), so sorting by it ranks
        // stocks by their most recent past payment — not what
        // users mean when they say "ex-div today first". Switch to
        // `next_ex_date` which is the UPCOMING ex-div, matching
        // the user's mental model.
        ex_div: parseDate(stockData?.next_ex_date) ?? parseDate(stockData?.ex_dividend_date) ?? -Infinity,
        div_pay: parseDate(stockData?.next_payment_date) ?? -Infinity,
        return_pct: returnPct,
        gain_loss: gainLoss,
      };
    });
    const dirMul = portSortDir === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const va = a[portSortKey];
      const vb = b[portSortKey];
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dirMul;
      const aBad = !isFinite(va);
      const bBad = !isFinite(vb);
      if (aBad && bBad) return 0;
      if (aBad) return 1;
      if (bBad) return -1;
      return (va - vb) * dirMul;
    });
    return rows.map((r) => r.ticker);
  })();

  if (activeTickers.length === 0) {
    return (
      <div id="portfolio-stocks-section" className="space-y-3" data-testid="portfolio-view-empty">
        {!isAllReadOnly && (
          <div className="mb-2">
            <QuickStockAddButton onPositionsChanged={onPositionsChanged} />
          </div>
        )}
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg font-medium">No positions yet</p>
          <p className="text-sm mt-1">
            {isAllReadOnly
              ? 'Pick a portfolio from the dropdown above to add your first stock.'
              : 'Use Quick Stock Add above, or click any stock and log a buy in its "My Portfolio" tab.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="portfolio-stocks-section" className="space-y-3" data-testid="portfolio-view">
      <DustLotsBanner onCleared={onPositionsChanged} />
      {/* Round P136 (Feb 2026) — Leverage concentration banner. Amber
          alert when > 30% of active positions (by count) are in the
          curated structurally-leveraged CEF set. Meant as a "you're
          stacked on leverage" nudge, not a hard warning — leverage
          isn't inherently bad, but concentration in it is worth
          knowing about. Uses the shared useLeveragedCefs hook so no
          extra fetch is triggered. */}
      {(() => {
        const levSet = leveragedCefSet;
        const levPositions = activeTickers.filter(
          (t) => (stockDataMap[t]?.is_cef || stocks.find((s) => s.ticker === t)?.is_cef)
            && levSet.has(String(t).toUpperCase())
        );
        const totalActive = activeTickers.length;
        if (totalActive === 0 || levPositions.length === 0) return null;
        const pct = (levPositions.length / totalActive) * 100;
        if (pct <= 30) return null;
        return (
          <div
            data-testid="portfolio-leverage-callout"
            className="flex items-start gap-3 rounded-lg border border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-sm"
          >
            <Zap className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0 text-amber-900 dark:text-amber-100">
              <span className="font-semibold">
                {levPositions.length} of {totalActive} positions ({pct.toFixed(0)}%)
              </span>{' '}
              use structural leverage — {levPositions.slice(0, 5).join(', ')}
              {levPositions.length > 5 ? `, +${levPositions.length - 5} more` : ''}.
              These CEFs use borrowed capital (typically 25-45% effective leverage) to boost yield, which amplifies both distributions AND drawdowns.
              {' '}
              <Link
                to="/glossary/cef-strategies"
                className="underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300"
              >
                See the Leverage lens →
              </Link>
            </div>
          </div>
        );
      })()}
      {/* Cut Alert — fires when ANY owned ticker has flipped to
          recent_div_change_type === 'cut' AND the user hasn't dismissed
          this specific event yet. Dismissals are keyed by `ticker +
          last_payment_date` so a future cut on the same stock re-fires
          the banner. localStorage so it persists across reloads but
          stays per-device. */}
      {(() => {
        const cutAlerts = activeTickers
          .map(t => stockDataMap[t])
          .filter(s => s && s.recent_div_change_type === 'cut' && s.recent_div_last_date);
        if (cutAlerts.length === 0) return null;
        const visible = cutAlerts.filter(s => {
          const key = `dividrip.cut.dismissed.${s.ticker}.${s.recent_div_last_date}`;
          try { return localStorage.getItem(key) !== '1'; } catch { return true; }
        });
        if (visible.length === 0) return null;
        return (
          <div
            className="flex items-start gap-3 rounded-lg border border-rose-300/70 dark:border-rose-700/60 bg-rose-50 dark:bg-rose-950/30 px-4 py-3"
            data-testid="portfolio-cut-alert-banner"
            role="alert"
          >
            <TrendingDown className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-rose-800 dark:text-rose-200">
                {visible.length === 1
                  ? `${visible[0].ticker} just cut its dividend by ${Math.abs(visible[0].recent_div_change_pct).toFixed(1)}%`
                  : `${visible.length} of your tracked stocks recently cut their dividend`}
              </p>
              <p className="text-xs text-rose-700/90 dark:text-rose-300/90 mt-0.5 leading-snug">
                {visible.map((s, i) => (
                  <span key={s.ticker}>
                    <button
                      type="button"
                      onClick={() => onStockClick?.(s.ticker)}
                      className="font-mono font-bold underline underline-offset-2 hover:opacity-80"
                      data-testid={`cut-alert-ticker-${s.ticker}`}
                    >
                      {s.ticker}
                    </button>
                    {' '}
                    <span className="font-mono">{s.recent_div_change_pct.toFixed(1)}%</span>
                    {' '}({s.recent_div_last_date})
                    {i < visible.length - 1 ? ' · ' : ''}
                  </span>
                ))}
                {' — review your position before the next ex-div date.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                visible.forEach(s => {
                  try {
                    localStorage.setItem(
                      `dividrip.cut.dismissed.${s.ticker}.${s.recent_div_last_date}`,
                      '1',
                    );
                  } catch {}
                });
                // Bump priceRefreshTick so the component re-evaluates
                // and the dismissed banner unmounts immediately.
                setPriceRefreshTick(t => t + 1);
              }}
              className="shrink-0 p-1 rounded hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 transition-colors"
              title="Dismiss this alert"
              data-testid="cut-alert-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })()}

      {/* Round P112d (Jul 31, 2026) — Thin-history warning banner.
          Any owned position whose backing stock row is flagged
          `frequency_inferred_thin` (single Massive payment observed,
          annualized cadence unconfirmed) surfaces here BEFORE the
          user buys more shares based on a projection. Dismissible per
          ticker via localStorage so the banner doesn't re-nag once the
          user has acknowledged. It re-fires if a fresh thin ticker
          enters the portfolio because the dismissal key includes the
          ticker itself. Soft-amber styling (not red) — this is a
          "know before you buy more" warning, not a cut alert. */}
      {(() => {
        const thinAlerts = activeTickers
          .map(t => stockDataMap[t] || stocks.find(s => s.ticker === t))
          .filter(s => s && s.frequency_inferred_thin);
        if (thinAlerts.length === 0) return null;
        const visible = thinAlerts.filter(s => {
          const key = `dividrip.thin.dismissed.${s.ticker}`;
          try { return localStorage.getItem(key) !== '1'; } catch { return true; }
        });
        if (visible.length === 0) return null;
        return (
          <div
            className="flex items-start gap-3 rounded-lg border border-amber-400/70 dark:border-amber-600/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3"
            data-testid="portfolio-thin-history-banner"
            role="alert"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
                {visible.length === 1
                  ? `${visible[0].ticker} — single dividend payment observed, annualized cadence unconfirmed`
                  : `${visible.length} of your positions have unconfirmed dividend cadence`}
              </p>
              <p className="text-xs text-amber-800/90 dark:text-amber-200/90 mt-0.5 leading-snug">
                Only one dividend has been recorded so far, so the projected annual income shown across the site is a conservative default (Annually × last payment). Treat any yield or DRIP projection for these holdings as provisional until a second payment posts.
                {' '}
                {visible.map((s, i) => (
                  <span key={s.ticker}>
                    <button
                      type="button"
                      onClick={() => onStockClick?.(s.ticker)}
                      className="font-mono font-bold underline underline-offset-2 hover:opacity-80"
                      data-testid={`thin-history-ticker-${s.ticker}`}
                    >
                      {s.ticker}
                    </button>
                    {i < visible.length - 1 ? ' · ' : ''}
                  </span>
                ))}
                {' — click through to Dividend Info for the full caveat.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                visible.forEach(s => {
                  try { localStorage.setItem(`dividrip.thin.dismissed.${s.ticker}`, '1'); } catch { /* localStorage disabled */ }
                });
                setPriceRefreshTick(t => t + 1);
              }}
              className="shrink-0 p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-300 transition-colors"
              title="Dismiss this warning (per-ticker, this device)"
              data-testid="portfolio-thin-history-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })()}

      {/* Round 86 (Feb 2026) — Concentration banner relocated to
          PortfolioSummary (renders under Cash Balance) per user
          layout request. Kept fetch above for ticker-row risk pills
          (see `riskData?.tickers?.[ticker]` further down). */}

      {/* Round P158 (Jun 2026) — Quick Stock Add. Sits under the
          banners and above the News / Badge Key / Portfolio: X/Y
          header row per user spec. Opens a modal with a compare-style
          ticker search + the real My Portfolio add form (quickMode). */}
      {!isAllReadOnly && (
        <div className="mb-2">
          <QuickStockAddButton onPositionsChanged={onPositionsChanged} />
        </div>
      )}

      {/* Capacity indicator + Refresh All. The "X/Y" pill mirrors the
          watchlist count badge on the All-Stocks Watchlist view, so users
          have a consistent place to see how full their tracked lists are.
          Mobile keeps both items on the same row to save vertical space. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {/* News toggle — May 2026: relocated from the page-wide
              action bar into the portfolio header bar, immediately
              before the "Portfolio: X/Y" capacity pill, per user
              request. Toggles inline article rendering across the
              portfolio rows below. */}
          {onNewsToggle && (
            <button
              type="button"
              onClick={onNewsToggle}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${
                showNews
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid="portfolio-news-toggle"
              title={showNews ? 'Hide inline news per position' : 'Show inline news per position'}
            >
              <Newspaper className={`w-4 h-4 shrink-0 ${showNews ? '' : 'text-blue-500 dark:text-cyan-400'}`} />
              <span>News</span>
            </button>
          )}
          {/* Round 96c (Jul 2026) — Badge Key relocated from the
              Dashboard toolbar into the Portfolio header, sitting to
              the right of the News button and to the left of the
              "Portfolio: X/Y" capacity pill per user request. Keeps
              the top toolbar clean (Screener + Challenge only) while
              putting the badge legend within thumb's reach of the
              positions the badges actually annotate. */}
          <BadgeKey />
          <span
            className="text-xs text-muted-foreground"
            data-testid="portfolio-capacity-indicator"
            title={
              userLimits?.portfolio_limit && userLimits.portfolio_limit !== userLimits.portfolio_default
                ? `Custom portfolio cap (default ${userLimits.portfolio_default}). Contact support to adjust.`
                : 'Distinct stocks across your portfolio. Contact support to request a higher cap.'
            }
          >
            Portfolio: <span className="font-mono font-semibold text-foreground">{activeTickers.length}/{userLimits?.portfolio_limit ?? 20}</span>
          </span>
        </div>
        {/* "Refresh All" button removed 2026-05-02 — it was calling the
            per-ticker force-refresh endpoint for every position at once,
            which in combination with background enrichment ran up $45+
            of legacy Finimpulse credits in a single click. Users can still hit
            the per-stock RefreshCw button beside each position (rate-
            limited to once per 5 min server-side). */}
      </div>

      {/* Round 96j (Jul 2026) — Portfolio sort bar. Sits directly
          under the header row (News / Badge Key / Portfolio: X/Y)
          and lets users re-order their portfolio rows by any of the
          key columns that live inside each StockBar. On phones it
          scrolls horizontally so all 8 chips remain reachable
          without wrapping. Selection persists to localStorage. */}
      <div
        className="mb-3 -mx-1 px-1 flex items-center gap-1.5 overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/30"
        style={{ scrollbarWidth: 'thin' }}
        data-testid="portfolio-sort-bar"
      >
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground shrink-0 mr-1">Sort</span>
        {[
          { k: 'name', label: 'Name' },
          { k: 'shares', label: 'Shares' },
          { k: 'purchased', label: 'Purchased' },
          { k: 'value', label: 'Value' },
          { k: 'ex_div', label: 'Ex-Div Date' },
          { k: 'div_pay', label: 'Div Pay' },
          { k: 'return_pct', label: 'Return %' },
          { k: 'gain_loss', label: 'G/L' },
        ].map(({ k, label }) => {
          const active = portSortKey === k;
          const arrow = active ? (portSortDir === 'asc' ? '▲' : '▼') : '';
          return (
            <button
              key={k}
              type="button"
              onClick={() => onPortSortClick(k)}
              className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                active
                  ? 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border border-fuchsia-500/40'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent'
              }`}
              data-testid={`portfolio-sort-${k}`}
              title={`Sort portfolio rows by ${label}${active ? ` (${portSortDir === 'asc' ? 'ascending' : 'descending'}) — click again to flip` : ''}`}
            >
              {label}
              {arrow && <span className="font-mono text-[9px] leading-none">{arrow}</span>}
            </button>
          );
        })}
      </div>

      {/* Portfolio-wide news vibe roll-up — only when user has news
          toggled on AND there's enough material (>= 4 articles) to
          give a meaningful aggregate. On phones, the source-mix and
          vibe pills wrap onto separate rows naturally via
          `flex-wrap` so layout stays clean. */}
      {showNews && allArticles.length >= 4 && (
        <div
          className="mb-3 rounded-lg border border-violet-300/40 dark:border-violet-700/30 bg-violet-50/50 dark:bg-violet-950/20 px-3 py-2"
          data-testid="portfolio-news-vibe-rollup"
        >
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider text-violet-900 dark:text-violet-200">
              Your Portfolio Vibe
            </span>
            <span className="text-[10px] text-muted-foreground">
              · across {allArticles.length} article{allArticles.length === 1 ? '' : 's'} from {activeTickers.length} holding{activeTickers.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <NewsSentimentSummary articles={allArticles} />
            <NewsSourceMix articles={allArticles} />
          </div>
        </div>
      )}
      {sortedActiveTickers.map(ticker => (
        <StockBar
          key={ticker}
          ticker={ticker}
          // Prefer freshly-fetched stockDataMap (live prices) over the
          // potentially-stale `stocks` prop so the portfolio page always
          // shows current prices — especially after login / coming back
          // to the site.
          stockData={stockDataMap[ticker] || stocks.find(s => s.ticker === ticker)}
          positions={allPositions[ticker]}
          onRemovePosition={(posId) => handleRemove(ticker, posId)}
          onSellPosition={handleSell}
          onMarkedDrip={handleMarkedDrip}
          onAccountTypeChanged={handleAccountTypeChanged}
          onBrokerChanged={handleBrokerChanged}
          onStockClick={onStockClick}
          onRefresh={handleRefresh}
          refreshing={refreshingTickers.has(ticker)}
          showNews={showNews}
          risk={riskData?.tickers?.[ticker]}
          dismissedPayoutDates={dismissedMap[ticker]}
          onPayoutActioned={handlePayoutActioned}
          isAllReadOnly={isAllReadOnly}
          actualPayoutsByDate={lastPayoutByTicker[ticker]}
          chartLastPayout={chartLastPayoutByTicker[ticker]}
          chartPayouts={chartPayoutsByTicker[ticker]}
          onMultiSold={() => handleMultiSold(ticker)}
        />
      ))}
    </div>
  );
}
